import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Upload, X, ArrowLeft, ArrowRight, Plus, Trash2 } from 'lucide-react';
import type { Database } from '@/lib/database.types';
import { useTranslation } from 'react-i18next';
import { batchOptimizeImages } from '@/utils/imageProcessor';
import { RichTextEditor } from '@/components/RichTextEditor';

type Product = Database['public']['Tables']['products']['Row'];
type Category = Database['public']['Tables']['categories']['Row'];
type Producer = Database['public']['Tables']['producers']['Row'];
type Ingredient = Database['public']['Tables']['ingredients']['Row'];

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

type Unit = 'g' | 'kg' | 'dl' | 'l';

interface FormData {
  name: string;
  description: string;
  price: string;
  pricePerKg: string;
  stockLevel: string;
  categoryId: string;
  weight: string;
  unit: Unit;
  storageLocation: string;
  specialHandling: string;
  isPublished: boolean;
  vatRate: number;
}

interface ProductFormSharedProps {
  productId?: string;
  producerId?: string;
  onSuccess?: (productId: string) => void;
  onCancel?: () => void;
  isAdmin?: boolean;
}

export function ProductFormShared({
  productId,
  producerId,
  onSuccess,
  onCancel,
  isAdmin = false
}: ProductFormSharedProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [producer, setProducer] = useState<Producer | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [existingUrls, setExistingUrls] = useState<string[]>([]);
  const [ingredients, setIngredients] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    price: '',
    pricePerKg: '',
    stockLevel: '',
    categoryId: '',
    weight: '',
    unit: 'kg',
    storageLocation: '',
    specialHandling: '',
    isPublished: false,
    vatRate: 12,
  });

  useEffect(() => {
    if (!producerId && !productId) return;

    async function fetchData() {
      try {
        setLoading(true);
        
        // First fetch the producer to get details about the producer
        if (producerId) {
          const { data: producerData, error: producerError } = await supabase
            .from('producers')
            .select('*')
            .eq('id', producerId)
            .single();

          if (producerError) {
            logger.error('Error fetching producer:', producerError);
            throw new Error('Failed to load producer details.');
          }

          setProducer(producerData);
        }

        // Fetch all categories for the dropdown
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (categoriesError) {
          logger.error('Error fetching categories:', categoriesError);
          throw new Error('Failed to load categories.');
        }

        setCategories(categoriesData || []);

        // If we're editing an existing product, fetch its details
        if (productId) {
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('*')
            .eq('id', productId)
            .single();

          if (productError) {
            logger.error('Error fetching product:', productError);
            throw new Error('Failed to load product details.');
          }

          if (productData) {
            // Populate the form with the product's data
            const formValues: FormData = {
              name: productData.name || '',
              description: productData.description || '',
              price: productData.price?.toString() || '',
              stockLevel: productData.stock_level?.toString() || '',
              storageLocation: productData.storage_location || '',
              specialHandling: productData.special_handling || '',
              categoryId: productData.category_id || '',
              pricePerKg: productData.price_per_kg?.toString() || '',
              isPublished: productData.is_published || false,
              weight: productData.weight?.toString() || '',
              unit: (productData.unit as Unit) || 'kg',
              vatRate: productData.vat_rate || 12
            };
            setFormData(formValues);

            // Set existing image URLs
            const urls = productData.image_urls || [];
            setPreviews(urls);
            setExistingUrls(urls);
            
            // Fetch ingredients for this product
            const { data: ingredientsData, error: ingredientsError } = await supabase
              .from('ingredients')
              .select('*')
              .eq('product_id', productId)
              .order('order_index');

            if (ingredientsError) {
              logger.error('Error fetching ingredients:', ingredientsError);
            } else {
              setIngredients(ingredientsData.map(i => i.name).join('\n') || '');
            }
          }
        }
      } catch (err) {
        logger.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [productId, producerId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const selectedFiles = Array.from(fileList).filter(file => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setUploadError('Only JPEG, PNG, and SVG files are allowed');
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        setUploadError('Files must be less than 5MB');
        return false;
      }
      return true;
    });

    if (selectedFiles.length === 0) return;

    setFiles(prev => [...prev, ...selectedFiles]);

    // Create previews
    selectedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index: number) => {
    // Check if the file is a new upload or an existing image
    const isExistingImage = index < existingUrls.length;
    
    if (isExistingImage) {
      // Remove from existing URLs
      setExistingUrls(prev => prev.filter((_, i) => i !== index));
    } else {
      // Adjust index for files array (which only contains new uploads)
      const fileIndex = index - existingUrls.length;
      setFiles(prev => prev.filter((_, i) => i !== fileIndex));
    }
    
    // Remove from previews (which contains both existing and new)
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const moveImage = (index: number, direction: 'left' | 'right') => {
    if (
      (direction === 'left' && index === 0) || 
      (direction === 'right' && index === previews.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'left' ? index - 1 : index + 1;
    
    // Create copies of the arrays
    const newPreviews = [...previews];
    const newExistingUrls = [...existingUrls];
    const newFiles = [...files];
    
    // Ensure the arrays have valid elements at the indices
    if (newPreviews[index] && newPreviews[newIndex]) {
      [newPreviews[index], newPreviews[newIndex]] = [newPreviews[newIndex], newPreviews[index]];
      setPreviews(newPreviews);
    }
    
    // Determine if we're dealing with existing images or new files
    const isCurrentExisting = index < existingUrls.length;
    const isTargetExisting = newIndex < existingUrls.length;
    
    if (isCurrentExisting && isTargetExisting) {
      // Both are existing images - swap in existingUrls
      [newExistingUrls[index], newExistingUrls[newIndex]] = [newExistingUrls[newIndex], newExistingUrls[index]];
      setExistingUrls(newExistingUrls);
    } 
    else if (!isCurrentExisting && !isTargetExisting) {
      // Both are new files - swap in files array
      const fileIndex = index - existingUrls.length;
      const newFileIndex = newIndex - existingUrls.length;
      [newFiles[fileIndex], newFiles[newFileIndex]] = [newFiles[newFileIndex], newFiles[fileIndex]];
      setFiles(newFiles);
    }
  };

  const uploadImages = async () => {
    if (files.length === 0 && existingUrls.length === 0) {
      return [];
    }

    // If there are only existing URLs and no new files, return the existing URLs
    if (files.length === 0) {
      return existingUrls;
    }

    try {
      // Optimize images before upload
      const optimizedFiles = files.length > 0 ? await batchOptimizeImages(files) : [];
      logger.debug(`Optimized ${optimizedFiles.length} files for upload`);
      
      // Upload each file to Supabase Storage
      const uploads = optimizedFiles.map(async (file) => {
        // Create a more sanitized filename to handle all special characters
        // Extract the file extension
        const fileExt = file.name.split('.').pop() || '';
        // Generate a completely sanitized name with timestamp and random string
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        logger.debug(`Attempting to upload file: ${fileName}, type: ${file.type}, size: ${Math.round(file.size/1024)}KB`);
        
        // Prepare upload options with explicit content type
        const options = {
          contentType: file.type
        };
        
        const { data, error } = await supabase.storage
          .from('product_images')
          .upload(`products/${fileName}`, file, options);

        if (error) {
          logger.error(`Failed to upload ${fileName}:`, error);
          if (error.message) {
            logger.error('Error message:', error.message);
          }
          throw error;
        }

        logger.debug('Upload successful:', data);
        // Get the public URL for the uploaded file - use the same path
        const { data: { publicUrl } } = supabase.storage
          .from('product_images')
          .getPublicUrl(`products/${fileName}`);

        return publicUrl;
      });

      // Wait for all uploads to complete
      let newUrls: string[] = [];
      try {
        newUrls = await Promise.all(uploads);
      } catch (uploadError) {
        logger.error('Error during Promise.all for uploads:', uploadError);
        throw uploadError;
      }
      
      // Combine existing URLs with new URLs
      return [...existingUrls, ...newUrls];
    } catch (error) {
      logger.error('Error in uploadImages:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Upload images first
      const imageUrls = await uploadImages();

      // Extract ingredient names into an array
      const ingredientList = ingredients
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      // Prepare product data
      const productData = {
        producer_id: producerId,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        price_per_kg: formData.pricePerKg ? parseFloat(formData.pricePerKg) : null,
        stock_level: parseInt(formData.stockLevel) || 0,
        category_id: formData.categoryId || null,
        storage_location: formData.storageLocation || null,
        special_handling: formData.specialHandling || null,
        image_urls: imageUrls,
        is_published: formData.isPublished,
        weight: parseFloat(formData.weight) || 0,
        unit: formData.unit,
        vat_rate: formData.vatRate,
        slug: formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now().toString().slice(-6)
      };

      let updatedProductId = productId;

      if (productId) {
        // Update existing product
        const { error: updateError } = await supabase
          .from('products')
          .update(productData)
          .eq('id', productId);

        if (updateError) {
          throw updateError;
        }

        // Delete existing ingredients and add new ones
        await supabase
          .from('ingredients')
          .delete()
          .eq('product_id', productId);
      } else {
        // Create new product
        const { data: newProduct, error: insertError } = await supabase
          .from('products')
          .insert(productData)
          .select('id')
          .single();

        if (insertError) {
          throw insertError;
        }

        updatedProductId = newProduct.id;
      }

      // Add ingredients if there are any
      if (ingredientList.length > 0 && updatedProductId) {
        const ingredientsToInsert = ingredientList.map((name, index) => ({
          product_id: updatedProductId,
          name,
          order_index: index
        }));

        const { error: ingredientsError } = await supabase
          .from('ingredients')
          .insert(ingredientsToInsert);

        if (ingredientsError) {
          logger.error('Error adding ingredients:', ingredientsError);
        }
      }

      if (onSuccess && updatedProductId) {
        onSuccess(updatedProductId);
      } else {
        // Navigate back to appropriate page based on user role
        const redirectPath = isAdmin
          ? `/admin/producers/${producerId}/products`
          : '/producer/products';
        navigate(redirectPath);
      }
    } catch (err) {
      logger.error('Error saving product:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!productId) return;
    
    setDeleteLoading(true);
    try {
      // Delete ingredients first (foreign key constraint)
      await supabase
        .from('ingredients')
        .delete()
        .eq('product_id', productId);
      
      // Then delete the product
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);
      
      if (error) throw error;
      
      // Navigate back
      const redirectPath = isAdmin
        ? `/admin/producers/${producerId}/products`
        : '/producer/products';
      navigate(redirectPath);
    } catch (err) {
      logger.error('Error deleting product:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="bg-white shadow-md rounded px-8 py-6">
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4 border-b pb-2">Produktinformation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
                Namn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="category">
                Kategori
              </label>
              <select
                id="category"
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('välj')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="description">
              Beskrivning
            </label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Beskrivning av varan"
              className="mt-1"
              height={200}
            />
          </div>
        </div>

        {/* Pricing and Stock */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4 border-b pb-2">Pris och Lager</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="price">
                Pris (kr) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="price"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pricePerKg">
                Pris per kg (kr/kg)
              </label>
              <input
                type="number"
                id="pricePerKg"
                value={formData.pricePerKg}
                onChange={(e) => setFormData({ ...formData, pricePerKg: e.target.value })}
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="stockLevel">
                Lagersaldo <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="stockLevel"
                value={formData.stockLevel}
                onChange={(e) => setFormData({ ...formData, stockLevel: e.target.value })}
                min="0"
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Momssats <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-4">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="vat6"
                  name="vatRate"
                  value="6"
                  checked={formData.vatRate === 6}
                  onChange={() => setFormData({ ...formData, vatRate: 6 })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="vat6" className="ml-2 block text-sm text-gray-900">
                  6%
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="vat12"
                  name="vatRate"
                  value="12"
                  checked={formData.vatRate === 12}
                  onChange={() => setFormData({ ...formData, vatRate: 12 })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="vat12" className="ml-2 block text-sm text-gray-900">
                  12%
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="vat25"
                  name="vatRate"
                  value="25"
                  checked={formData.vatRate === 25}
                  onChange={() => setFormData({ ...formData, vatRate: 25 })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="vat25" className="ml-2 block text-sm text-gray-900">
                  25%
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="weight">
                Vikt/Volym
              </label>
              <input
                type="number"
                id="weight"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="unit">
                Enhet
              </label>
              <select
                id="unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value as Unit })}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="dl">dl</option>
                <option value="l">l</option>
              </select>
            </div>
          </div>
        </div>

        {/* Handling and Storage */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4 border-b pb-2">Hantering och Förvaring</h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="storageLocation">
                Typ av vara
              </label>
              <input
                type="text"
                id="storageLocation"
                value={formData.storageLocation}
                onChange={(e) => setFormData({ ...formData, storageLocation: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Frysvara, Kylvara eller liknande"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="specialHandling">
                Särskilda hanteringsinstruktioner
              </label>
              <RichTextEditor
                value={formData.specialHandling}
                onChange={(value) => setFormData({ ...formData, specialHandling: value })}
                placeholder="Förvaring, tillagning eller liknande"
                className="mt-1"
                height={150}
              />
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4 border-b pb-2">Ingredienser</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="ingredients">
              Ingredienslista
            </label>
            <RichTextEditor
              value={ingredients}
              onChange={(value) => setIngredients(value)}
              placeholder="Ingredienser"
              className="mt-1"
              height={200}
            />
            <p className="text-xs text-gray-500 mt-1">Ange allergener också. Viktigt för kunder med allergier.</p>
          </div>
        </div>

        {/* Product Images */}
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-4 border-b pb-2">Bilder</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ladda upp bilder
            </label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-10 h-10 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Klicka för att ladda upp</span>
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, JPEG, SVG, WebP upp till 5MB</p>
                </div>
                <input 
                  id="dropzone-file" 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {uploadError && <p className="text-red-500 text-sm mt-2">{uploadError}</p>}
          </div>

          {/* Image Preview Grid */}
          {previews.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Förhandsgranskning</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative bg-gray-100 p-2 rounded border border-gray-200">
                    <img 
                      src={preview} 
                      alt={`Förhandsgranskning ${index + 1}`} 
                      className="w-full h-32 object-contain"
                    />
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="absolute bottom-2 right-2 flex space-x-1">
                      <button
                        type="button"
                        onClick={() => moveImage(index, 'left')}
                        disabled={index === 0}
                        className={`bg-gray-200 p-1 rounded-full ${
                          index === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'
                        }`}
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveImage(index, 'right')}
                        disabled={index === previews.length - 1}
                        className={`bg-gray-200 p-1 rounded-full ${
                          index === previews.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'
                        }`}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Publish Status */}
        <div className="mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isPublished"
              checked={formData.isPublished}
              onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900">
              Publicera vara
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">När publicerad blir varan synlig för kunder att beställa.</p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-8">
          <div>
            {productId && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
                disabled={loading || deleteLoading}
              >
                {deleteLoading ? 'Tar bort...' : 'Ta bort'}
              </button>
            )}
          </div>
          
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onCancel || (() => {
                const backPath = isAdmin
                  ? `/admin/producers/${producerId}/products`
                  : '/producer/products';
                navigate(backPath);
              })}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded"
              disabled={loading}
            >
              Avbryt
            </button>
            
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
              disabled={loading}
            >
              {loading ? 'Sparar...' : (productId ? 'Spara produkt' : 'Skapa produkt')}
            </button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Bekräfta borttagning</h3>
            <p className="mb-6">Är du säker på att du vill ta bort denna vara? Detta kan inte ångras.</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded"
              >
                Avbryt
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Tar bort...' : 'Ta bort'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 